import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mic, Square, Loader2, Send, Sparkles, Undo2, AlertTriangle, Wand2, RefreshCw, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, eur } from "@/lib/studioApi";

const FALLBACK_SUGGESTIONS = [
  "Ajoute deux heures de main-d'œuvre",
  "Mets 6 % de TVA partout",
  "Ajoute le déplacement et la mise en service",
  "Ajoute l'évacuation des déchets",
  "Propose une option avec du matériel haut de gamme",
  "Ajoute un acompte de 30 % à la commande",
];

// Dictée libre : Whisper transcrit, le texte part comme instruction.
const useDictation = (onText) => {
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecording(false);
        if (blob.size < 1200) return toast.error("Enregistrement trop court.");
        setSending(true);
        const fd = new FormData();
        fd.append("file", new File([blob], `ordre.${mimeType.includes("webm") ? "webm" : "mp4"}`, { type: mimeType }));
        try {
          const { data } = await studio.post("/transcribe", fd, { timeout: 180000 });
          onText(data.text);
        } catch (e) {
          toast.error(apiError(e, "Transcription impossible."));
        } finally {
          setSending(false);
        }
      };
      mediaRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      toast.error("Micro inaccessible. Autorisez l'accès au micro.");
    }
  };

  return { recording, sending, start, stop: () => mediaRef.current?.stop() };
};

export const QuoteAssistant = ({ quoteId, lines, setLines, testId = "quote-assistant" }) => {
  const [params, setParams] = useSearchParams();
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [profit, setProfit] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const booted = useRef(false);

  const loadSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const { data } = await studio.post(`/quotes/${quoteId}/suggestions`, { lines: linesRef.current }, { timeout: 120000 });
      setSuggestions(data.suggestions?.length ? data.suggestions : FALLBACK_SUGGESTIONS);
    } catch {
      setSuggestions(FALLBACK_SUGGESTIONS);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const run = async (text) => {
    const order = (text ?? instruction).trim();
    if (order.length < 3) return toast.error("Dites ce que vous voulez changer.");
    setBusy(true);
    const current = linesRef.current;
    const before = current.map((l) => ({ ...l }));
    try {
      const { data } = await studio.post(
        `/quotes/${quoteId}/assistant`,
        { instruction: order, lines: current },
        { timeout: 300000 },
      );
      setLines(data.lines.map((l) => ({ ...l })));
      setUndoStack((s) => [before, ...s].slice(0, 10));
      setProfit(data.profitability);
      setHistory((h) => [
        { order, summary: data.summary, changes: data.changes, question: data.question, notes: data.notes },
        ...h,
      ].slice(0, 12));
      setInstruction("");
      if (data.suggestions?.length) setSuggestions(data.suggestions);
      toast.success(data.question ? "L'assistant a besoin d'une précision." : "Devis mis à jour — pensez à enregistrer.");
    } catch (e) {
      toast.error(apiError(e, "L'assistant n'a pas pu appliquer cette demande."));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (booted.current || !quoteId) return;
    booted.current = true;
    const preset = params.get("instruction");
    if (preset) {
      params.delete("instruction");
      setParams(params, { replace: true });
      run(preset);
    } else {
      loadSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const dictation = useDictation((text) => {
    setInstruction(text);
    run(text);
  });

  const undo = () => {
    const [previous, ...rest] = undoStack;
    if (!previous) return;
    setLines(previous.map((l) => ({ ...l })));
    setUndoStack(rest);
    setHistory(history.slice(1));
    setProfit(null);
    toast.success("Modification annulée.");
  };

  return (
    <div data-testid={testId} className="mt-4 overflow-hidden rounded-2xl border border-[#3A5CC8]/25 bg-white shadow-[0_1px_3px_rgba(11,30,61,0.04)]">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E2E7F7] bg-gradient-to-r from-blue-50 to-white px-4 py-3.5 sm:px-5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#3A5CC8] text-white">
            <Wand2 className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="font-heading text-base font-bold text-[#0F2645]">Dites-lui quoi changer</h2>
            <p className="truncate text-xs text-[#5C6A87]">Parlez ou écrivez, il modifie les lignes du devis</p>
          </div>
        </div>
        {!!undoStack.length && (
          <button
            type="button"
            data-testid="assistant-undo"
            onClick={undo}
            className="inline-flex min-h-[38px] shrink-0 items-center gap-1.5 rounded-xl border border-[#E2E7F7] px-3.5 text-xs font-bold text-[#5C6A87] hover:border-[#3A5CC8] hover:text-[#3A5CC8]"
          >
            <Undo2 className="h-3.5 w-3.5" /> Annuler
          </button>
        )}
      </div>

      <div className="px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <textarea
            data-testid="assistant-input"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                run();
              }
            }}
            rows={2}
            placeholder="Ex. : ajoute 3 mètres de tubage inox et deux heures de main-d'œuvre"
            className="min-h-[64px] w-full flex-1 resize-none rounded-xl border border-[#E2E7F7] bg-white px-3.5 py-3 text-[15px] text-[#0F2645] placeholder:text-[#93A0BC] focus:border-[#3A5CC8] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]/30"
          />
          <div className="flex gap-2">
          <button
            type="button"
            data-testid="assistant-mic"
            disabled={busy || dictation.sending}
            onClick={dictation.recording ? dictation.stop : dictation.start}
            className={`inline-flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl transition-colors ${
              dictation.recording ? "bg-rose-600 text-white" : "border border-[#E2E7F7] text-[#3A5CC8] hover:border-[#3A5CC8]"
            } disabled:opacity-40`}
          >
            {dictation.sending ? <Loader2 className="h-5 w-5 animate-spin" /> : dictation.recording ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
          </button>
          <button
            type="button"
            data-testid="assistant-send"
            disabled={busy || instruction.trim().length < 3}
            onClick={() => run()}
            className="inline-flex h-[52px] flex-1 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#3A5CC8] px-5 sm:flex-none text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Appliquer
          </button>
          </div>
        </div>

        {dictation.recording && (
          <p data-testid="assistant-recording" className="mt-2 inline-flex items-center gap-2 text-xs font-bold text-rose-600">
            <span className="h-2 w-2 animate-pulse rounded-full bg-rose-600" /> J'écoute… appuyez sur le carré pour terminer.
          </p>
        )}

        <div data-testid="assistant-suggestions" className="mt-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#93A0BC]">
              <Lightbulb className="h-3.5 w-3.5 text-amber-500" /> {history.length ? "Et ensuite ? Suggestions pour ce devis" : "Suggestions pour compléter ce devis"}
            </p>
            <button
              type="button"
              data-testid="assistant-suggestions-refresh"
              onClick={loadSuggestions}
              disabled={busy || loadingSuggestions}
              className="inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[11px] font-semibold text-[#5C6A87] hover:bg-[#F3F5FC] hover:text-[#3A5CC8] disabled:opacity-40"
            >
              <RefreshCw className={`h-3 w-3 ${loadingSuggestions ? "animate-spin" : ""}`} /> Autres idées
            </button>
          </div>
          {loadingSuggestions && !suggestions.length ? (
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4].map((i) => <span key={i} className="h-8 w-40 animate-pulse rounded-full bg-[#F3F5FC]" style={{ width: `${120 + (i % 3) * 40}px` }} />)}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`assistant-suggestion-${i + 1}`}
                  onClick={() => run(s)}
                  disabled={busy}
                  className="rounded-lg border border-[#E2E7F7] bg-[#F6F8FD] px-3 py-1.5 text-left text-xs font-medium text-[#0F2645] transition-colors hover:border-[#3A5CC8] hover:bg-[#EEF1FC] hover:text-[#2F4CA8] disabled:opacity-40"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {profit && (profit.at_loss || profit.below_target) && (
          <div
            data-testid="assistant-margin-alert"
            className={`mt-3 flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 ${
              profit.at_loss ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"
            }`}
          >
            <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${profit.at_loss ? "text-rose-600" : "text-amber-600"}`} />
            <p className={`text-sm leading-relaxed ${profit.at_loss ? "text-rose-900" : "text-amber-900"}`}>
              {profit.at_loss
                ? `Attention : ce devis est à perte (${eur(profit.margin)}).`
                : `Marge à ${profit.margin_percent} % (${eur(profit.margin)}), sous votre objectif de ${profit.target_margin_percent} %.`}
              <span className="block text-xs opacity-80">
                Matériel {eur(profit.material_cost)} · main-d'œuvre {profit.labour_hours} h ({eur(profit.labour_cost)})
              </span>
            </p>
          </div>
        )}

        {!!history.length && (
          <div data-testid="assistant-history" className="mt-4 space-y-2.5">
            {history.map((h, i) => (
              <div key={i} className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD]/70 px-3.5 py-3">
                <p className="text-xs font-bold uppercase tracking-wider text-[#93A0BC]">« {h.order} »</p>
                {h.summary && (
                  <p className="mt-1.5 inline-flex items-start gap-1.5 text-sm font-semibold text-[#0F2645]">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#3A5CC8]" /> {h.summary}
                  </p>
                )}
                {!!h.changes?.length && (
                  <ul className="mt-1.5 space-y-1">
                    {h.changes.map((c, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs text-[#5C6A87]">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                        {c}
                      </li>
                    ))}
                  </ul>
                )}
                {h.question && <p className="mt-1.5 text-xs font-semibold text-amber-700">{h.question}</p>}
                {h.notes && <p className="mt-1 text-xs italic text-[#93A0BC]">{h.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
